import React from "react";
import { useTranslation } from "react-i18next";
import { Clock, Mail, ShieldCheck } from "lucide-react";

import { cn } from "@/lib/utils";

const SUPPORT_EMAIL = "info@chatharmony.ai";

type AccentColor = "indigo" | "slate" | "emerald";

const accentStyles: Record<AccentColor, { iconWrap: string; icon: string }> = {
  indigo: {
    iconWrap: "bg-indigo-500/10 dark:bg-indigo-500/15",
    icon: "text-indigo-600 dark:text-indigo-400",
  },
  slate: {
    iconWrap: "bg-slate-500/10 dark:bg-slate-500/15",
    icon: "text-slate-600 dark:text-slate-400",
  },
  emerald: {
    iconWrap: "bg-emerald-500/10 dark:bg-emerald-500/15",
    icon: "text-emerald-600 dark:text-emerald-400",
  },
};

const InfoRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  color: AccentColor;
  children: React.ReactNode;
}> = ({ icon, title, color, children }) => {
  const styles = accentStyles[color];

  return (
    <div className="flex items-start gap-3 p-5">
      <div
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
          styles.iconWrap,
        )}
      >
        <span className={styles.icon}>{icon}</span>
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <div className="text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
};

export const SupportInfoAside: React.FC = () => {
  const { t } = useTranslation();

  return (
    <aside className="flex flex-col gap-4 lg:self-start">
      {/* Sol kolondaki başlık satırı arama kutusu nedeniyle 32px (h-8);
          aynı yükseklik verilerek iki kart üstten hizalanır */}
      <h2 className="flex h-8 items-center text-base font-semibold text-foreground">
        {t("support.aside.title")}
      </h2>

      {/* Tek kart + ince ayraçlar: 3 ayrı renkli kutu yerine daha sakin, kurumsal görünüm */}
      <div className="divide-y divide-border rounded-2xl border border-border bg-card shadow-sm">
        <InfoRow
          icon={<Mail className="h-4 w-4" />}
          title={t("support.aside.emailTitle")}
          color="indigo"
        >
          <p>{t("support.aside.emailDescription")}</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-1 inline-block font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {SUPPORT_EMAIL}
          </a>
        </InfoRow>

        <InfoRow
          icon={<Clock className="h-4 w-4" />}
          title={t("support.aside.responseTitle")}
          color="slate"
        >
          <p>{t("support.aside.responseDescription")}</p>
        </InfoRow>

        <InfoRow
          icon={<ShieldCheck className="h-4 w-4" />}
          title={t("support.aside.privacyTitle")}
          color="emerald"
        >
          <p>{t("support.aside.privacyDescription")}</p>
        </InfoRow>
      </div>
    </aside>
  );
};
